import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Download, Image as ImageIcon } from "lucide-react";

const statusBadge = (s) => {
  const map = {
    limpio: { cls: "lx-badge-success", label: "Limpio" },
    requiere_atencion: { cls: "lx-badge-warning", label: "Requiere atención" },
    insumos_faltantes: { cls: "lx-badge-warning", label: "Insumos faltantes" },
    problema: { cls: "lx-badge-error", label: "Problema" },
  };
  const m = map[s] || { cls: "lx-badge-muted", label: s };
  return <span className={`lx-badge ${m.cls}`}>{m.label}</span>;
};

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-MX", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" });
  } catch { return iso; }
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function HistoryPage() {
  const { user } = useAuth();
  const isClient = user?.role === "cliente";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [locations, setLocations] = useState([]);
  const [areas, setAreas] = useState([]);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ client_id: "", location_id: "", area_id: "", user_id: "", status: "", date_from: "", date_to: "" });
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    (async () => {
      const [cli, loc, ar, us] = await Promise.all([
        api.get("/clients"), api.get("/locations"), api.get("/areas"),
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      setClients(cli.data); setLocations(loc.data); setAreas(ar.data); setUsers(us.data);
    })();
  }, []);

  // For cliente role, /api/users returns 403, so derive distinct users from the checkins they can see
  const allUsers = useMemo(() => {
    if (users.length > 0) return users;
    const map = new Map();
    items.forEach(c => {
      if (c.user_id && !map.has(c.user_id)) map.set(c.user_id, { id: c.user_id, name: c.user_name || c.user_id });
    });
    return [...map.values()].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [users, items]);

  const load = async () => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v));
    const { data } = await api.get("/checkins", { params });
    setItems(data); setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const filteredLocations = useMemo(() => filters.client_id ? locations.filter(l => l.client_id === filters.client_id) : locations, [locations, filters.client_id]);
  const filteredAreas = useMemo(() => {
    if (filters.location_id) return areas.filter(a => a.location_id === filters.location_id);
    if (filters.client_id) {
      const locIds = filteredLocations.map(l => l.id);
      return areas.filter(a => locIds.includes(a.location_id));
    }
    return areas;
  }, [areas, filters.location_id, filters.client_id, filteredLocations]);

  const exportCSV = () => {
    const head = ["Fecha","Cliente","Sucursal","Área","Usuario","Estatus","Observaciones","Foto"];
    const rows = items.map(c => [
      fmt(c.created_at), clients.find(x=>x.id===c.client_id)?.name || "", c.location_name || "",
      c.area_name || "", c.user_name || "", c.status || "", (c.notes || "").replace(/[\n\r,]/g, " "),
      c.photo_url ? `${BACKEND}${c.photo_url}` : ""
    ]);
    const csv = [head, ...rows].map(r => r.map(x => `"${String(x ?? "").replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `historial-limpio-express-${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="lx-overline">Registros</div>
          <h1 className="lx-section-title mt-1">Historial de check-ins</h1>
          <p className="text-lx-muted text-sm mt-1">Todos los registros de limpieza con filtros avanzados.</p>
        </div>
        <button className="lx-btn lx-btn-secondary h-[42px]" onClick={exportCSV} data-testid="export-csv-button">
          <Download size={16}/> Exportar CSV
        </button>
      </div>

      <div className="lx-card">
        <div className={`grid grid-cols-2 sm:grid-cols-3 ${isClient ? "lg:grid-cols-6" : "lg:grid-cols-7"} gap-3`}>
          {!isClient && (
            <Select label="Cliente" value={filters.client_id} onChange={(v)=>setFilters({...filters, client_id: v, location_id: "", area_id: ""})} options={clients.map(c=>({v:c.id,l:c.name}))} testid="filter-client"/>
          )}
          <Select label="Sucursal" value={filters.location_id} onChange={(v)=>setFilters({...filters, location_id: v, area_id: ""})} options={filteredLocations.map(l=>({v:l.id,l:l.name}))} testid="filter-location"/>
          <Select label="Área" value={filters.area_id} onChange={(v)=>setFilters({...filters, area_id: v})} options={filteredAreas.map(a=>({v:a.id,l:a.name}))} testid="filter-area"/>
          <Select label="Usuario" value={filters.user_id} onChange={(v)=>setFilters({...filters, user_id: v})} options={allUsers.map(u=>({v:u.id,l:u.name}))} testid="filter-user"/>
          <Select label="Estatus" value={filters.status} onChange={(v)=>setFilters({...filters, status: v})} options={[
            {v:"limpio", l:"Limpio"}, {v:"requiere_atencion", l:"Requiere atención"},
            {v:"insumos_faltantes", l:"Insumos faltantes"}, {v:"problema", l:"Problema"}
          ]} testid="filter-status"/>
          <div>
            <label className="lx-label">Desde</label>
            <input type="date" className="lx-input" value={filters.date_from} onChange={(e)=>setFilters({...filters, date_from: e.target.value})} data-testid="filter-date-from"/>
          </div>
          <div>
            <label className="lx-label">Hasta</label>
            <input type="date" className="lx-input" value={filters.date_to} onChange={(e)=>setFilters({...filters, date_to: e.target.value})} data-testid="filter-date-to"/>
          </div>
        </div>
      </div>

      <div className="lx-card p-0 overflow-hidden">
        {loading ? <div className="p-6 text-lx-muted">Cargando…</div> : items.length === 0 ? (
          <div className="p-10 text-center text-lx-muted">No se encontraron registros con los filtros aplicados.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="lx-table">
              <thead>
                <tr><th>Fecha</th><th>Área</th><th>Sucursal</th><th>Usuario</th><th>Estatus</th><th>Evidencia</th><th>Notas</th></tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id} className="cursor-pointer" onClick={()=>setDetail(c)} data-testid={`history-row-${c.id}`}>
                    <td className="text-lx-muted">{fmt(c.created_at)}</td>
                    <td className="font-medium">{c.area_name || "—"}</td>
                    <td>{c.location_name || "—"}</td>
                    <td>{c.user_name || "—"}</td>
                    <td>{statusBadge(c.status)}</td>
                    <td>{c.photo_url ? <ImageIcon size={16} className="text-lx-primary"/> : <span className="text-lx-muted">—</span>}</td>
                    <td className="text-lx-muted text-xs max-w-xs truncate">{c.notes || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {detail && <DetailModal item={detail} onClose={()=>setDetail(null)} />}
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="lx-label">{label}</label>
      <select className="lx-input" value={value} onChange={(e)=>onChange(e.target.value)} data-testid={testid}>
        <option value="">Todos</option>
        {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}

function DetailModal({ item, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e)=>e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-lx-border">
          <h3 className="font-heading font-bold text-lg">Detalle del registro</h3>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <Row k="Fecha" v={fmt(item.created_at)}/>
          <Row k="Área" v={item.area_name}/>
          <Row k="Sucursal" v={item.location_name}/>
          <Row k="Usuario" v={item.user_name}/>
          <Row k="Estatus" v={statusBadge(item.status)}/>
          <Row k="Observaciones" v={item.notes || "—"}/>
          {(item.latitude && item.longitude) && <Row k="GPS" v={`${item.latitude}, ${item.longitude}`}/>}
          <Row k="Dispositivo" v={<span className="text-xs text-lx-muted">{item.device_info || "—"}</span>}/>
          {item.photo_url && (
            <div>
              <div className="lx-label">Evidencia</div>
              <img src={`${BACKEND}${item.photo_url}`} alt="Evidencia" className="rounded-lg border border-lx-border max-h-80" data-testid="detail-photo"/>
            </div>
          )}
          <div className="pt-3 flex justify-end">
            <button className="lx-btn lx-btn-secondary" onClick={onClose}>Cerrar</button>
          </div>
        </div>
      </div>
    </div>
  );
}
const Row = ({ k, v }) => <div className="flex justify-between gap-4"><span className="text-lx-muted">{k}</span><span className="font-medium text-right">{v}</span></div>;
